import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Grid, Snackbar, Alert } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import emailjs from 'emailjs-com';

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: '',
  });

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: '',
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' }); // Clear error for the current field
  };

  const validateForm = () => {
    let formIsValid = true;
    const newErrors = { ...errors };

    // Validate first name
    if (!formData.firstName) {
      formIsValid = false;
      newErrors.firstName = 'First Name is required.';
    }

    // Validate last name
    if (!formData.lastName) {
      formIsValid = false;
      newErrors.lastName = 'Last Name is required.';
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      formIsValid = false;
      newErrors.email = 'Email is required.';
    } else if (!emailRegex.test(formData.email)) {
      formIsValid = false;
      newErrors.email = 'Invalid email format.';
    }

    // Validate message
    if (!formData.message) {
      formIsValid = false;
      newErrors.message = 'Message cannot be empty.';
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate the form
    if (!validateForm()) {
      setSnackbarMessage('Please fix the errors in the form.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // Send email using EmailJS
    emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID!,
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID!,
      formData,
      process.env.REACT_APP_EMAILJS_USER_ID!
    )
    .then(() => {
      setSnackbarMessage('Message sent successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setFormData({ firstName: '', lastName: '', email: '', message: '' }); // Reset form
    }, (err) => {
      setSnackbarMessage('Failed to send message. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    });
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ marginTop: "30px" }}>
      <Typography variant="h4" gutterBottom sx={{ textAlign: "center", color: "#001530" }} className='component'>
        Have Some <span style={{ background: "linear-gradient(-96deg, rgb(194 183 183 / 0%) 0%, rgb(255 249 220 / 4%) 28%, rgb(255, 162, 49) 100%, rgb(255 255 255 / 0%) 100%)", fontWeight: "normal" }}>Questions About</span> Our Services?
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '20px',
        }}
      >
        <Grid container spacing={2} justifyContent="center" alignItems="center">
          {/* Left Section with Icon */}
          <Grid item xs={12} md={6} sx={{ textAlign: 'center', justifyContent: "center", display: "flex" }}>
            <Box
            className='component'
              component="img"
              src="emailen.webp" // Add your icon or SVG here
              alt="Envelope"
              sx={{ width: '45%', height: 'auto', marginBottom: '20px', marginTop: "20px" }}
            />
          </Grid>

          {/* Right Section with Form */}
          <Grid item xs={12} md={6}>
            <Box
            className='component'
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                maxWidth: '400px',
                margin: 'auto',
              }}
              noValidate
            >
              <TextField
                label="First Name"
                variant="outlined"
                fullWidth
                required
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                error={!!errors.firstName}
                helperText={errors.firstName}
              />
              <TextField
                label="Last Name"
                variant="outlined"
                fullWidth
                required
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                error={!!errors.lastName}
                helperText={errors.lastName}
              />
              <TextField
                label="What's your email?"
                variant="outlined"
                fullWidth
                required
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
              />
              <TextField
                label="Your questions..."
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                required
                name="message"
                value={formData.message}
                onChange={handleChange}
                error={!!errors.message}
                helperText={errors.message}
              />
              <Button
                variant="contained"
                endIcon={<SendIcon />}
                sx={{
                  backgroundColor: "#f97316"
                }}
                type="submit" // Add submit type to the button
              >
                SEND MESSAGE
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Snackbar for messages */}
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactForm;
